/**
 * Build public Market Finder (homepage) search URLs from assistant reply text.
 */

const US_STATE_NAMES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
];

const LOB_HINTS = [
  { pattern: /commercial\s+auto/i, name: 'Commercial Auto' },
  { pattern: /workers?\s*comp/i, name: 'Workers Compensation' },
  { pattern: /general\s+liability/i, name: 'General Liability' },
  { pattern: /farm\s*(?:&|and)\s*ranch/i, name: 'Farm & Ranch' },
  { pattern: /crop/i, name: 'Crop' },
  { pattern: /homeowners?/i, name: 'Homeowners' },
  { pattern: /property/i, name: 'Property' },
];

function markdownToPlain(text) {
  return String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[#*_`>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** @returns {{ query?: string, state?: string, lineOfBusiness?: string, companySlug?: string }} */
export function extractMarketFinderSearchHints(text) {
  const raw = String(text || '');
  const plain = markdownToPlain(raw);
  if (!plain) return {};

  const slugMatch = raw.match(/\/company\/([a-z0-9-]+)/i);
  const companySlug = slugMatch ? slugMatch[1] : undefined;

  let state;
  for (const name of US_STATE_NAMES) {
    if (new RegExp(`\\b${name.replace(/ /g, '\\s+')}\\b`, 'i').test(plain)) {
      state = name;
      break;
    }
  }

  let lineOfBusiness;
  for (const hint of LOB_HINTS) {
    if (hint.pattern.test(plain)) {
      lineOfBusiness = hint.name;
      break;
    }
  }

  let query = companySlug ? companySlug.replace(/-/g, ' ') : '';
  if (!query) {
    const tableRow = plain.match(/\|\s*([^|]{2,60}?)\s*\|/);
    if (tableRow && !/^[-–—]+$/.test(tableRow[1].trim())) {
      query = tableRow[1].trim();
    }
  }
  if (!query) {
    query = plain.split(/[.!?\n]/)[0].trim().slice(0, 72);
  }

  return {
    query: query || undefined,
    state,
    lineOfBusiness,
    companySlug,
  };
}

export function buildMarketFinderSearchPath(hints = {}) {
  if (hints.companySlug) {
    return `/company/${encodeURIComponent(hints.companySlug)}`;
  }

  const params = new URLSearchParams();
  params.set('page', '1');
  params.set('per_page', '24');

  if (hints.state) params.set('state', hints.state);
  if (hints.lineOfBusiness) {
    params.set('line_of_business', hints.lineOfBusiness);
  }
  if (hints.query) params.set('query', hints.query);

  if (hints.state || hints.lineOfBusiness) {
    params.set('searchByLob', 'true');
  }

  return `/?${params.toString()}`;
}

export function openMarketFinderSearchFromAssistantText(text) {
  const hints = extractMarketFinderSearchHints(text);
  const path = buildMarketFinderSearchPath(hints);
  window.open(path, '_blank', 'noopener,noreferrer');
}
